import React, { useEffect } from "react"
import {
  VStack,
  FormControl,
  FormLabel,
  Select,
} from "@chakra-ui/react"
import {
  PrinterModelLabels,
  PrinterMediaLabels,
  getAvailablePrinterModels,
  getCompatibleMediaForPrinter,
  isCompatibleCombination,
} from "components/BadgeBuilder/utils/printers"
import { PrinterModel, PrinterMedia } from "sharedTypes/badge"

type Props = {
  printerModel: PrinterModel
  printerMedia: PrinterMedia | ""
  onPrinterModelChange: (model: PrinterModel) => void
  onPrinterMediaChange: (media: PrinterMedia | "") => void
  isDisabled?: boolean
}

const PrinterLabelSelectionForm = ({
  printerModel,
  printerMedia,
  onPrinterModelChange,
  onPrinterMediaChange,
  isDisabled = false,
}: Props) => {
  const availableModels = getAvailablePrinterModels()
  const compatibleMedia = getCompatibleMediaForPrinter(printerModel)

  // Auto-reset media when printer changes and current media is incompatible
  // OR auto-select when there's only one compatible media option
  useEffect(() => {
    if (printerMedia && !isCompatibleCombination(printerModel, printerMedia)) {
      // Reset if incompatible
      onPrinterMediaChange("")
    } else if (compatibleMedia.length === 1 && printerMedia !== compatibleMedia[0]) {
      // Auto-select if there's only one option
      onPrinterMediaChange(compatibleMedia[0])
    }
  }, [printerModel, printerMedia, onPrinterMediaChange, compatibleMedia])

  // Disable media select if there's only one option (e.g., Generic printer)
  const isMediaSelectDisabled = isDisabled || compatibleMedia.length === 1

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel fontWeight="semibold">Printer Model</FormLabel>
        <Select
          value={printerModel}
          onChange={(e) => onPrinterModelChange(e.target.value as PrinterModel)}
          isDisabled={isDisabled}
        >
          {availableModels.map((model) => (
            <option key={model} value={model}>
              {PrinterModelLabels[model]}
            </option>
          ))}
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel fontWeight="semibold">Media Type</FormLabel>
        <Select
          value={printerMedia}
          onChange={(e) => onPrinterMediaChange(e.target.value as PrinterMedia | "")}
          isDisabled={isMediaSelectDisabled}
          placeholder="Select a media type"
        >
          {compatibleMedia.map((media) => (
            <option key={media} value={media}>
              {PrinterMediaLabels[media]}
            </option>
          ))}
        </Select>
      </FormControl>
    </VStack>
  )
}

export default PrinterLabelSelectionForm
